import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ExternalLink, TrendingUp, Zap, Target, Clock, ChevronDown, ChevronUp, Check, ArrowRight, CheckCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import storycraftLogo from "@/assets/storycraft-logo-new.png";

// --- Types ---
interface ReportStep { number: number; title: string; content: string; manual_time: string; benchmark: string; }
interface ReportPhase { phase: string; tasks: string[]; }
interface ReportAction { number: number; title: string; description: string; }
interface ReportSection { id: string; title: string; content?: string; steps?: ReportStep[]; phases?: ReportPhase[]; actions?: ReportAction[]; }
interface Report { plan_title: string; situation_summary: string; point_a: string; point_b: string; sections: ReportSection[]; }
interface QuizReportProps { report: Report; unlocked: boolean; email: string; setEmail: (v: string) => void; gateName: string; setGateName: (v: string) => void; emailSubmitting: boolean; handleEmailSubmit: (e: React.FormEvent) => void; answers: Record<string, string | string[]>; }

// --- Scroll reveal ---
function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}
function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useReveal(0.1);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

// --- Scoring ---
const SECTION_META: Record<number, { maxScore: number; icon: React.ReactNode }> = {
  0: { maxScore: 25, icon: <Target className="w-4 h-4" /> },
  1: { maxScore: 20, icon: <Zap className="w-4 h-4" /> },
  2: { maxScore: 25, icon: <TrendingUp className="w-4 h-4" /> },
  3: { maxScore: 30, icon: <Target className="w-4 h-4" /> },
  4: { maxScore: 25, icon: <Zap className="w-4 h-4" /> },
  5: { maxScore: 30, icon: <Clock className="w-4 h-4" /> },
  6: { maxScore: 20, icon: <Zap className="w-4 h-4" /> },
  7: { maxScore: 20, icon: <TrendingUp className="w-4 h-4" /> },
};

function computeSectionScore(index: number, answers: Record<string, string | string[]>): number {
  const meta = SECTION_META[index] || { maxScore: 25 };
  const max = meta.maxScore;
  switch (index) {
    case 0: { let s = 8; const aud = answers.audience_size as string; if (aud?.includes("Over 100,000")) s += 10; else if (aud?.includes("20,000")) s += 8; else if (aud?.includes("5,000")) s += 6; else if (aud?.includes("500 –")) s += 3; const freq = answers.posting_frequency as string; if (freq?.includes("Daily")) s += 7; else if (freq?.includes("4–6")) s += 5; else if (freq?.includes("1–3")) s += 3; return Math.min(s, max); }
    case 1: { let s = 5; const blocker = answers.blocker as string; if (blocker && blocker.length > 80) s += 10; else if (blocker && blocker.length > 30) s += 6; else if (blocker) s += 3; const hist = answers.monetisation_history as string; if (hist?.includes("tried but")) s += 5; else if (hist?.includes("little money")) s += 3; return Math.min(s, max); }
    case 2: { let s = 8; const goal = answers.goal as string; if (goal?.includes("Scale")) s += 12; else if (goal?.includes("Replace")) s += 10; else if (goal?.includes("Launch")) s += 8; else if (goal?.includes("Build")) s += 6; const hours = answers.hours_per_week as string; if (hours?.includes("20+")) s += 5; else if (hours?.includes("10–20")) s += 4; else if (hours?.includes("5–10")) s += 3; return Math.min(s, max); }
    case 3: { let s = 12; const plats = answers.platforms as string[]; if (plats && plats.length >= 3) s += 10; else if (plats && plats.length >= 2) s += 6; else if (plats) s += 3; const niche = answers.niche as string; if (niche && niche.length > 10) s += 8; else if (niche) s += 4; return Math.min(s, max); }
    case 4: { let s = 8; const hist = answers.monetisation_history as string; if (hist?.includes("product/service")) s += 12; else if (hist?.includes("little money")) s += 8; else if (hist?.includes("tried")) s += 5; else s += 3; const tried = answers.what_they_tried as string; if (tried && tried.length > 50) s += 5; return Math.min(s, max); }
    case 5: { let s = 10; const hours = answers.hours_per_week as string; if (hours?.includes("20+")) s += 12; else if (hours?.includes("10–20")) s += 10; else if (hours?.includes("5–10")) s += 8; else if (hours?.includes("2–5")) s += 5; else s += 2; const stage = answers.stage as string; if (stage?.includes("scaling") || stage?.includes("making some")) s += 8; else if (stage?.includes("growing audience")) s += 5; else s += 2; return Math.min(s, max); }
    case 6: { let s = 10; const plats = answers.platforms as string[]; if (plats && plats.length >= 2) s += 5; const niche = answers.niche as string; if (niche && niche.length > 5) s += 5; return Math.min(s, max); }
    case 7: { let s = 6; const fear = answers.biggest_fear as string; if (fear && fear.length > 80) s += 10; else if (fear && fear.length > 30) s += 6; else if (fear) s += 3; return Math.min(s, max); }
    default: return Math.round(max * 0.5);
  }
}

function getGrade(score: number, max: number) {
  const pct = (score / max) * 100;
  if (pct >= 80) return { letter: "A", color: "#16A34A", bgColor: "#DCFCE7", barColor: "#16A34A" };
  if (pct >= 65) return { letter: "B", color: "#2563EB", bgColor: "#DBEAFE", barColor: "#2563EB" };
  if (pct >= 50) return { letter: "C", color: "#D97706", bgColor: "#FEF3C7", barColor: "#D97706" };
  if (pct >= 35) return { letter: "D", color: "#DC2626", bgColor: "#FEE2E2", barColor: "#DC2626" };
  return { letter: "F", color: "#DC2626", bgColor: "#FEE2E2", barColor: "#DC2626" };
}

function getImpactTag(sectionIndex: number, itemIndex: number) {
  if (sectionIndex === 3 || sectionIndex === 4 || sectionIndex === 5) {
    if (itemIndex < 2) return { label: "High Impact", bg: "#7C3AED", fg: "#FFFFFF" };
    return { label: "Medium Impact", bg: "#F59E0B", fg: "#FFFFFF" };
  }
  if (sectionIndex === 6) return { label: "High Impact", bg: "#7C3AED", fg: "#FFFFFF" };
  if (itemIndex === 0) return { label: "High Impact", bg: "#7C3AED", fg: "#FFFFFF" };
  if (itemIndex < 3) return { label: "Medium Impact", bg: "#F59E0B", fg: "#FFFFFF" };
  return { label: "Low Impact", bg: "#6B7280", fg: "#FFFFFF" };
}

function getEffortTag(sectionIndex: number, itemIndex: number) {
  if (sectionIndex === 6 || sectionIndex === 7) return { label: "Quick Fix", bg: "#16A34A", fg: "#FFFFFF" };
  if (sectionIndex === 0 || sectionIndex === 1) return { label: "Some Effort", bg: "#2563EB", fg: "#FFFFFF" };
  if (itemIndex === 0) return { label: "Quick Fix", bg: "#16A34A", fg: "#FFFFFF" };
  if (itemIndex < 3) return { label: "Some Effort", bg: "#2563EB", fg: "#FFFFFF" };
  return { label: "Major Effort", bg: "#DC2626", fg: "#FFFFFF" };
}

function getEstimatedImpact(sectionIndex: number, itemIndex: number): string {
  const impacts = [
    "Could increase your clarity and focus within 1 week",
    "May unlock 2–3x more engagement on your content",
    "Could generate your first sale within 14 days",
    "Expected to save 5+ hours per week on content creation",
    "Could add £200–£500/month in recurring revenue",
    "May double your email list growth rate",
    "Quick win — visible results within 48 hours",
    "Shifts your mindset for long-term consistency",
  ];
  return impacts[(sectionIndex + itemIndex) % impacts.length];
}

// --- Collapsible Section ---
function CollapsibleSection({ title, children, defaultOpen = false, index, scores }: { title: string; children: React.ReactNode; defaultOpen?: boolean; index: number; scores: any }) {
  const [open, setOpen] = useState(defaultOpen);
  const sc = scores[index];
  const pct = sc ? (sc.score / sc.max) * 100 : 0;

  return (
    <Reveal delay={index * 80}>
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ backgroundColor: "#F3F0FF", color: "#7C3AED" }}>
            {index + 1}
          </div>
          <div className="flex-1 h-px" style={{ background: "#E5E7EB" }} />
        </div>

        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md bg-white" style={{ border: "1px solid #E5E7EB" }}>
          <h2 className="text-[15px] md:text-base font-bold text-left" style={{ color: "#111827" }}>{title}</h2>
          <div className="flex items-center gap-3">
            {sc && (
              <div className="flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: sc.bgColor, color: sc.color }}>
                {sc.score}/{sc.max}
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold" style={{ backgroundColor: sc.color, color: "#FFFFFF" }}>
                  {sc.letter}
                </span>
              </div>
            )}
            {open ? <ChevronUp className="w-4 h-4" style={{ color: "#9CA3AF" }} /> : <ChevronDown className="w-4 h-4" style={{ color: "#9CA3AF" }} />}
          </div>
        </button>

        {sc && (
          <div className="w-full h-1.5 rounded-full overflow-hidden mt-2" style={{ background: "#E5E7EB" }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: sc.barColor }} />
          </div>
        )}

        {open && <div className="mt-4 pl-2">{children}</div>}
      </div>
    </Reveal>
  );
}

// --- Main Component ---
export default function QuizReport({ report, unlocked, email, setEmail, gateName, setGateName, emailSubmitting, handleEmailSubmit, answers }: QuizReportProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const sections = report.sections;
  const scores = sections.map((_, i) => {
    const meta = SECTION_META[i] || { maxScore: 25 };
    const score = computeSectionScore(i, answers);
    const grade = getGrade(score, meta.maxScore);
    return { score, max: meta.maxScore, ...grade };
  });
  const totalScore = scores.reduce((s, sc) => s + sc.score, 0);
  const totalMax = scores.reduce((s, sc) => s + sc.max, 0);
  const overallGrade = getGrade(totalScore, totalMax);

  const renderActionItems = (section: ReportSection, sectionIndex: number) => {
    const items: { number: number; title: string; description: string }[] = [];
    if (section.steps?.length) section.steps.forEach(s => items.push({ number: s.number, title: s.title, description: s.content }));
    else if (section.actions?.length) section.actions.forEach(a => items.push({ number: a.number, title: a.title, description: a.description }));
    else if (section.phases?.length) section.phases.forEach((p, i) => items.push({ number: i + 1, title: p.phase, description: p.tasks.join("\n• ") }));
    if (items.length === 0 && section.content) {
      section.content.split(/\n\n+/).filter(p => p.trim()).forEach((p, i) => {
        const match = p.match(/^(?:\d+[\.\)]\s*)?(.+)/);
        const title = match ? match[1].substring(0, 60) : `Point ${i + 1}`;
        items.push({ number: i + 1, title: title.split(".")[0] || title, description: p });
      });
    }
    if (items.length === 0) return null;
    return (
      <div className="space-y-3">
        {items.map((item, itemIdx) => {
          const impact = getImpactTag(sectionIndex, itemIdx);
          const effort = getEffortTag(sectionIndex, itemIdx);
          const estimated = getEstimatedImpact(sectionIndex, itemIdx);
          return (
            <div key={item.number} className="bg-white rounded-xl p-4 md:p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={{ border: "1px solid #E5E7EB" }}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: "#F3F0FF", color: "#7C3AED" }}>{item.number}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h4 className="font-semibold text-sm leading-tight" style={{ color: "#111827" }}>{item.title}</h4>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: impact.bg, color: impact.fg }}>{impact.label}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: effort.bg, color: effort.fg }}>{effort.label}</span>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none text-[13px] leading-relaxed" style={{ color: "#6B7280" }}>
                    <ReactMarkdown>{item.description}</ReactMarkdown>
                  </div>
                  <p className="text-[11px] italic mt-2" style={{ color: "#9CA3AF" }}>Estimated impact: {estimated}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSectionIntro = (section: ReportSection) => {
    if (!section.content) return null;
    const sentences = section.content.split(/(?<=[.!?])\s+/).slice(0, 3).join(" ");
    return <p className="text-[13px] leading-relaxed mb-4" style={{ color: "#6B7280" }}>{sentences}</p>;
  };

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuccess(true);
    // Brief success animation, then delegate to parent handler which redirects
    setTimeout(() => {
      handleEmailSubmit(e);
    }, 1000);
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAFAFA", color: "#111827" }}>
      <div className="mx-auto px-4 md:px-6 py-8 md:py-14" style={{ maxWidth: 820 }}>
        {/* Header */}
        <Reveal>
          <div className="text-center mb-8">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: "#F3F0FF", color: "#7C3AED" }}>
              YOUR PERSONALISED REPORT
            </div>
            <h1 className="text-[22px] md:text-3xl font-extrabold mb-3 leading-tight" style={{ color: "#111827" }}>{report.plan_title}</h1>
            <p className="text-sm" style={{ color: "#6B7280" }}>{report.situation_summary}</p>
          </div>
        </Reveal>

        {/* Overall Grade */}
        <Reveal delay={100}>
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-4 px-6 py-4 rounded-2xl bg-white shadow-sm" style={{ border: `2px solid ${overallGrade.color}30` }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-extrabold" style={{ backgroundColor: overallGrade.bgColor, color: overallGrade.color, boxShadow: `0 0 0 4px ${overallGrade.color}30` }}>
                {overallGrade.letter}
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: "#111827" }}>Overall Score</p>
                <p className="text-xs" style={{ color: "#6B7280" }}>{totalScore}/{totalMax} points</p>
              </div>
              <div className="hidden sm:flex items-end gap-1 ml-4 h-8">
                {scores.slice(0, 8).map((sc, i) => (
                  <div key={i} className="w-3 rounded-t transition-all" style={{ height: `${(sc.score / sc.max) * 100}%`, minHeight: 4, backgroundColor: sc.barColor, opacity: 0.8 }} />
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Point A / Point B */}
        <Reveal delay={200}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={{ border: "1px solid #E5E7EB", borderLeft: "4px solid #F59E0B" }}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#D97706" }}>Where You Are Now</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "#374151" }}>{report.point_a}</p>
            </div>
            <div className="bg-white rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={{ border: "1px solid #E5E7EB", borderLeft: "4px solid #7C3AED" }}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#7C3AED" }}>Where You Want To Be</h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "#374151" }}>{report.point_b}</p>
            </div>
          </div>
        </Reveal>

        {/* EMAIL GATE */}
        {!unlocked && (
          <Reveal delay={300}>
            <div className="mb-10">
              <div className="rounded-2xl p-6 md:p-8" style={{ backgroundColor: "#F5F3FF", border: "1px solid #E9E3FF" }}>
                {showSuccess ? (
                  <div className="flex flex-col items-center justify-center py-6">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "#DCFCE7" }}>
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-lg font-bold" style={{ color: "#111827" }}>Sent!</p>
                    <p className="text-sm mt-1" style={{ color: "#6B7280" }}>Check your inbox for the full report</p>
                  </div>
                ) : (
                  <>
                    <Lock className="w-8 h-8 mx-auto mb-3" style={{ color: "#7C3AED" }} />
                    <h3 className="text-xl font-bold mb-2 text-center" style={{ color: "#111827" }}>Get Your Complete 7-Section Roadmap</h3>
                    <p className="text-sm mb-5 text-center" style={{ color: "#6B7280" }}>
                      Your full personalised roadmap is ready. Enter your details and we'll send it straight to your inbox.
                    </p>

                    <div className="max-w-sm mx-auto space-y-2 mb-6">
                      {["Your personalised action plan", "Revenue targets & timeline", "Product recommendations"].map((b, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#16A34A" }} />
                          <span className="text-sm" style={{ color: "#374151" }}>{b}</span>
                        </div>
                      ))}
                    </div>

                    <form onSubmit={onFormSubmit} className="max-w-sm mx-auto space-y-3">
                      <input
                        placeholder="Your first name"
                        value={gateName}
                        onChange={e => setGateName(e.target.value)}
                        required
                        className="w-full h-12 px-4 rounded-xl text-sm bg-white outline-none transition-colors focus:ring-2 focus:ring-purple-300"
                        style={{ border: "1px solid #E5E7EB", color: "#111827" }}
                      />
                      <input type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full h-12 px-4 rounded-xl text-base bg-white outline-none transition-colors focus:ring-2 focus:ring-purple-300" style={{ border: "1px solid #E5E7EB", color: "#111827" }} />
                      <button type="submit" disabled={emailSubmitting} className="w-full font-bold text-base py-3.5 rounded-xl transition-all duration-200 border-none text-white cursor-pointer min-h-[52px] hover:scale-[1.02] active:scale-[0.98]" style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
                        {emailSubmitting ? "Sending..." : "Send My Full Report →"}
                      </button>
                    </form>

                    <p className="text-xs text-center mt-3" style={{ color: "#6B7280" }}>Join 1,200+ creators who've built their roadmap</p>
                    <p className="text-xs text-center mt-1" style={{ color: "#9CA3AF" }}>Free · No spam · Unsubscribe anytime</p>
                  </>
                )}
              </div>
            </div>
          </Reveal>
        )}

        {/* Sections - always blurred, report is email-only */}
        <div className="relative">
          {sections.map((section, index) => (
            <div key={section.id || index} style={{ filter: "blur(6px)", pointerEvents: "none", userSelect: "none" }}>
              <CollapsibleSection title={section.title} index={index} scores={scores} defaultOpen={index < 2}>
                {renderSectionIntro(section)}
                {renderActionItems(section, index)}
              </CollapsibleSection>
            </div>
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8" style={{ borderTop: "1px solid #E5E7EB" }}>
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <img src={storycraftLogo} alt="AI StoryCraft Studio" className="w-5 h-5 rounded-md object-contain" />
              <span className="text-xs" style={{ color: "#9CA3AF" }}>Powered by AI StoryCraft Studio</span>
            </div>
            <div className="flex items-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
              <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
